/**
 * Admin login page — OTP email flow + Google sign-in.
 *
 * Two-step OTP flow:
 * 1. Enter email → request OTP
 * 2. Enter 6-digit code → verify → JWT
 *
 * Google sign-in uses the Google Identity Services library loaded via script tag.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useAdminAuth } from '../auth/AdminAuthProvider';
import { MatcoraBrandMark } from '../components/MatcoraBrand';

type Step = 'email' | 'otp';

export function LoginPage() {
  const { requestOtp, login, loginWithGoogle, isLoading, error } = useAdminAuth();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const otpInputRef = useRef<HTMLInputElement>(null);

  const displayError = error || localError;
  const isMockMode = import.meta.env.VITE_ADMIN_MOCK === 'true';

  const handleRequestOtp = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setLocalError(null);
      if (!email.trim()) return;
      try {
        await requestOtp(email.trim());
        setStep('otp');
      } catch {
        // error already set in auth state
      }
    },
    [email, requestOtp],
  );

  const handleVerifyOtp = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setLocalError(null);
      if (!otp.trim()) return;
      try {
        await login(email.trim(), otp.trim());
      } catch {
        // error already set in auth state
      }
    },
    [email, otp, login],
  );

  // Focus OTP input when switching to step 2
  useEffect(() => {
    if (step === 'otp') {
      otpInputRef.current?.focus();
    }
  }, [step]);

  // Google sign-in callback
  const handleGoogleCredential = useCallback(
    async (response: { credential: string }) => {
      setLocalError(null);
      try {
        await loginWithGoogle(response.credential);
      } catch {
        // error already set in auth state
      }
    },
    [loginWithGoogle],
  );

  // Initialize Google Identity Services
  const googleBtnRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const clientId = import.meta.env.VITE_ADMIN_GOOGLE_CLIENT_ID;
    if (!clientId) return;

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => {
      const google = (window as unknown as Record<string, unknown>).google as {
        accounts: {
          id: {
            initialize: (opts: Record<string, unknown>) => void;
            renderButton: (el: HTMLElement, opts: Record<string, unknown>) => void;
          };
        };
      } | undefined;
      if (!google || !googleBtnRef.current) return;
      google.accounts.id.initialize({
        client_id: clientId,
        callback: handleGoogleCredential,
      });
      google.accounts.id.renderButton(googleBtnRef.current, {
        theme: 'outline',
        size: 'large',
        width: 320,
        text: 'signin_with',
      });
    };
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [handleGoogleCredential]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--surface-primary)] p-4">
      <div className="max-w-sm w-full relative">
        {/* Brand */}
        <div className="text-center mb-8">
          <MatcoraBrandMark className="mx-auto mb-5 h-24 w-24 shadow-[0_12px_24px_rgba(15,23,42,0.14)]" />
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">Matcora Admin</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">Sign in to your workspace</p>
          <p className="text-xs text-[var(--text-tertiary)] mt-2">
            Workspace analytics for your AI
          </p>
        </div>

        {/* Error */}
        {displayError && (
          <div
            role="alert"
            className="mb-4 p-3 text-sm rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-[var(--status-error)]"
          >
            {displayError}
          </div>
        )}

        {/* OTP flow */}
        {isMockMode && (
          <div className="mb-4 rounded-lg border border-[var(--border-default)] bg-[var(--surface-elevated)] px-3 py-2 text-sm text-[var(--text-secondary)] shadow-sm">
            Local mock mode is enabled. Use any email and verification code <strong>111111</strong>.
          </div>
        )}

        <div className="rounded-[1.5rem] border border-[var(--border-default)] bg-white p-5 shadow-[0_12px_28px_rgba(15,23,42,0.06)]">
        {step === 'email' ? (
          <form onSubmit={handleRequestOtp}>
            <label
              htmlFor="admin-email"
              className="block text-sm font-medium text-[var(--text-primary)] mb-1.5"
            >
              Email address
            </label>
            <input
              id="admin-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="form-input mb-4"
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={isLoading || !email.trim()}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[var(--accent-primary)] text-white font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {isLoading ? <Spinner /> : <MailIcon />}
              {isLoading ? 'Sending...' : 'Send sign-in code'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp}>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              We sent a code to <strong className="text-[var(--text-primary)]">{email}</strong>
            </p>
            <label
              htmlFor="admin-otp"
              className="block text-sm font-medium text-[var(--text-primary)] mb-1.5"
            >
              Verification code
            </label>
            <input
              ref={otpInputRef}
              id="admin-otp"
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              required
              autoComplete="one-time-code"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              className="form-input mb-4 text-center text-xl tracking-[0.3em] font-mono"
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={isLoading || otp.length !== 6}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[var(--accent-primary)] text-white font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {isLoading ? <Spinner /> : null}
              {isLoading ? 'Verifying...' : 'Verify & sign in'}
            </button>
            <button
              type="button"
              onClick={() => { setStep('email'); setOtp(''); }}
              className="w-full mt-2 px-4 py-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            >
              Use a different email
            </button>
          </form>
        )}

        {/* Divider */}
        {(import.meta.env.VITE_ADMIN_GOOGLE_CLIENT_ID || isMockMode) && (
          <>
            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-[var(--border-default)]" />
              <span className="text-xs text-[var(--text-tertiary)]">or</span>
              <div className="flex-1 h-px bg-[var(--border-default)]" />
            </div>

            {isMockMode ? (
              <button
                type="button"
                onClick={() => void handleGoogleCredential({ credential: 'mock-google-token' })}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-[var(--border-default)] bg-[var(--surface-elevated)] text-[var(--text-primary)] font-medium hover:bg-[var(--interactive-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <GoogleIcon />
                Sign in with Google (Mock)
              </button>
            ) : (
              <div className="flex justify-center">
                <div ref={googleBtnRef} />
              </div>
            )}
          </>
        )}
        </div>

        <p className="mt-8 text-center text-xs text-[var(--text-tertiary)]">
          Access restricted to invited team members.
        </p>
      </div>
    </div>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────

function MailIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7l-10 7L2 7" />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#EA4335" d="M12 10.2v3.9h5.4c-.2 1.3-1.6 3.9-5.4 3.9-3.3 0-5.9-2.7-5.9-6s2.6-6 5.9-6c1.9 0 3.1.8 3.8 1.4l2.6-2.5C16.7 3.3 14.6 2.5 12 2.5A9.5 9.5 0 0 0 2.5 12 9.5 9.5 0 0 0 12 21.5c5.5 0 9.1-3.9 9.1-9.4 0-.6-.1-1.1-.2-1.9H12Z" />
      <path fill="#34A853" d="M2.5 7.7 5.7 10c.9-2.6 3.4-4.4 6.3-4.4 1.9 0 3.1.8 3.8 1.4l2.6-2.5C16.7 3.3 14.6 2.5 12 2.5c-3.7 0-7 2.1-8.7 5.2Z" opacity=".9" />
      <path fill="#4285F4" d="M12 21.5c2.5 0 4.6-.8 6.2-2.3l-2.9-2.4c-.8.6-1.9 1.1-3.3 1.1-3.7 0-5.2-2.5-5.4-3.8l-3.1 2.4A9.5 9.5 0 0 0 12 21.5Z" opacity=".9" />
      <path fill="#FBBC05" d="M2.9 16.5A9.7 9.7 0 0 1 2.5 12c0-1.5.3-2.9.8-4.3L6.5 10a6 6 0 0 0-.3 2c0 .7.1 1.4.3 2.1l-3.6 2.4Z" opacity=".9" />
    </svg>
  );
}

function Spinner() {
  return (
    <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}
